import React from 'react';
import {
  Box,
  List,
  NoSearchItemsPlaceholder,
  PaginationBar,
  SearchPlaceholderIcon,
} from '@common-fe/common-fe';

import { SERVICE_TYPE_LABELS } from '../../../Services.constants';
import { ServiceItem } from '../../../Services.types';

import ServiceItemActions from './ServiceItemActions';
import ServicesListControls from './ServicesListControls';

interface ServicesListProps {
  services: ServiceItem[];
  total: number;
  isLoading: boolean;
  searchString: string;
  serviceTypeFilter: string;
  page: number;
  perPage: number;
  isEditAccessible: boolean;
  onSearchChange: (value: string) => void;
  onServiceTypeChange: (value: string) => void;
  onPageChange: (value: number) => void;
  onPerPageChange: (value: number) => void;
  onEditService: (service: ServiceItem) => void;
  onHideService: (service: ServiceItem) => void;
  onUnhideService: (service: ServiceItem) => void;
}

const ServicesList: React.FC<ServicesListProps> = ({
  services,
  total,
  isLoading,
  searchString,
  serviceTypeFilter,
  page,
  perPage,
  isEditAccessible,
  onSearchChange,
  onServiceTypeChange,
  onPageChange,
  onPerPageChange,
  onEditService,
  onHideService,
  onUnhideService,
}) => {
  return (
    <Box data-testid="services-list_wrapper" background="module" round="container1Round" pad="spacing24">
      <ServicesListControls
        total={total}
        searchString={searchString}
        serviceTypeFilter={serviceTypeFilter}
        onSearchChange={onSearchChange}
        onServiceTypeChange={onServiceTypeChange}
      />
      <List>
        {services.length > 0 && (
          <List.Header margin={{ bottom: 'spacing4' }} pad="spacing12">
            <List.Header.Item flex={5}>Service name</List.Header.Item>
            <List.Header.Item flex={4}>Type</List.Header.Item>
            {isEditAccessible && <List.Header.Item flex={1} />}
          </List.Header>
        )}
        {isLoading ? (
          <List.Preloader testId="services-list_loading" count={1} />
        ) : (
          <>
            {services.length === 0 && (
              <NoSearchItemsPlaceholder
                placeholderTitle="No services found"
                node={<SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />}
                style={{ height: 'auto' }}
              />
            )}
            {services.map((service) => (
              <List.Item
                key={service.id}
                data-testid={`services-list_item_${service.id}`}
                height="48px"
                margin={{ top: 'spacing4' }}
                pad={{ horizontal: 'spacing12' }}
                disableHover
              >
                <List.Item.Text weight={400} flex={5} data-testid={`services-list_item_${service.id}_name`}>
                  {service.name}
                </List.Item.Text>
                <List.Item.Text
                  weight={400}
                  flex={4}
                  color="textBody"
                  data-testid={`services-list_item_${service.id}_type`}
                >
                  {SERVICE_TYPE_LABELS[service.serviceType] || service.serviceType}
                </List.Item.Text>
                {isEditAccessible && (
                  <Box flex={{ shrink: 0 }} basis="auto" style={{ flex: 1 }}>
                    <ServiceItemActions
                      service={service}
                      onEdit={onEditService}
                      onHide={onHideService}
                      onUnhide={onUnhideService}
                    />
                  </Box>
                )}
              </List.Item>
            ))}
          </>
        )}
      </List>
      {total > perPage && (
        <PaginationBar
          page={page}
          total={total}
          pageSize={perPage}
          onChangePage={onPageChange}
          onChangePageSize={onPerPageChange}
        />
      )}
    </Box>
  );
};

export default ServicesList;
